import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Download } from "lucide-react";

import { api, downloadProtected } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Header } from "@/components/header";
import { Badge, Button, Card, Field } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";
import { C, date } from "@/lib/format";

const PROFILE = [
  { key: "last_name", label: "Nom" },
  { key: "first_name", label: "Prénom" },
  { key: "phone", label: "Téléphone" },
  { key: "agency", label: "Agence ou réseau" },
  { key: "city", label: "Ville" },
  { key: "department", label: "Département" },
];

export default function Account() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, setUser, logout } = useAuth();
  const [p, setP] = useState({});
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const del = useQuery({ queryKey: ["delete-request"], queryFn: () => api("/me/delete-request") });

  useEffect(() => {
    if (user) setP(Object.fromEntries(PROFILE.map((f) => [f.key, user[f.key] ?? ""])));
  }, [user]);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  if (!user) return null;
  return (
    <div data-testid="account-screen" className="flex flex-col">
      <Header title="Mon compte" subtitle={`Membre depuis le ${date(user.created_at)}`} />
      <div className="mx-auto grid w-full max-w-6xl items-start gap-4 p-4 pb-8 md:p-8 lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <h2 className="t-h3">Informations personnelles</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {PROFILE.map((f) => (
              <Field key={f.key} testID={`account-${f.key.replace("_", "-")}-input`} label={f.label} value={p[f.key] ?? ""} onChangeText={(t) => setP((x) => ({ ...x, [f.key]: t }))} />
            ))}
          </div>
          <Button testID="account-save-profile-button" title="Enregistrer" loading={busy === "profile"} onClick={() => run("profile", async () => {
            setUser(await api("/me/profile", { method: "PUT", body: p }));
            toast("Informations mises à jour.");
          })} />
        </Card>
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 className="t-h3">Adresse e-mail</h2>
              <Badge testID="account-email-status" label={user.email_verified ? "Confirmée" : "Non confirmée"} color={user.email_verified ? C.success : C.warning} />
            </div>
            <p data-testid="account-current-email" className="t-body">{user.email}</p>
            <Field testID="account-new-email-input" label="Nouvel e-mail" type="email" value={newEmail} onChangeText={setNewEmail} />
            <Field testID="account-email-password-input" label="Mot de passe actuel" type="password" value={emailPw} onChangeText={setEmailPw} />
            <Button testID="account-change-email-button" title="Changer d'e-mail" variant="secondary" loading={busy === "email"} onClick={() => run("email", async () => {
              setUser(await api("/me/email", { method: "PUT", body: { new_email: newEmail.trim(), password: emailPw } }));
              setNewEmail(""); setEmailPw("");
              toast("E-mail modifié. Un lien de confirmation vous a été envoyé.");
            })} />
          </Card>
          <Card className="flex flex-col gap-3">
            <h2 className="t-h3">Mot de passe</h2>
            <Field testID="account-current-password-input" label="Mot de passe actuel" type="password" value={curPw} onChangeText={setCurPw} />
            <Field testID="account-new-password-input" label="Nouveau mot de passe (8 caractères min.)" type="password" value={newPw} onChangeText={setNewPw} />
            <Button testID="account-change-password-button" title="Modifier le mot de passe" variant="secondary" loading={busy === "pw"} onClick={() => run("pw", async () => {
              await api("/me/password", { method: "PUT", body: { current_password: curPw, new_password: newPw } });
              setCurPw(""); setNewPw("");
              toast("Mot de passe modifié.");
            })} />
          </Card>
        </div>
        <Card className="flex flex-col gap-3">
          <h2 className="t-h3">Mes données</h2>
          <p className="t-body">Consultez l'historique de vos dossiers dans l'onglet Dossiers, ou téléchargez l'ensemble de vos informations.</p>
          <Button testID="account-export-button" title="Télécharger mes informations" icon={Download} variant="secondary" onClick={() => run("export", () => downloadProtected("/me/export", "mes-donnees-ma-maison-suivie.json"))} />
          <Button testID="account-history-button" title="Voir l'historique des recommandations" icon={Clock} variant="ghost" onClick={() => navigate("/dossiers")} />
        </Card>
        <Card className="flex flex-col gap-3 !border-[#EB5757]">
          <h2 className="t-h3 !text-[#EB5757]">Suppression du compte</h2>
          {del.data?.status === "pending" ? (
            <p data-testid="account-delete-pending" className="t-body">Demande envoyée le {date(del.data.created_at)} — en cours de traitement par le gérant.</p>
          ) : (
            <>
              <p className="t-body">Vos données personnelles seront anonymisées. Les dossiers financiers (commissions, paiements) sont conservés conformément aux obligations légales.</p>
              <Button testID="account-delete-request-button" title="Demander la suppression définitive" variant="danger" onClick={() => setDelOpen(true)} />
            </>
          )}
        </Card>
      </div>
      <Sheet visible={delOpen} onClose={() => setDelOpen(false)} title="Supprimer mon compte" testID="delete-sheet">
        <div className="flex flex-col gap-4">
          <p className="t-body">Cette demande sera transmise au gérant. Les commissions non payées doivent être réglées avant la suppression.</p>
          <Field testID="delete-reason-input" label="Motif (facultatif)" value={reason} onChangeText={setReason} multiline />
          <Button testID="delete-confirm-button" title="Confirmer la demande" variant="danger" loading={busy === "del"} onClick={() => run("del", async () => {
            await api("/me/delete-request", { method: "POST", body: { reason: reason || null } });
            setDelOpen(false);
            del.refetch();
            toast("Demande de suppression envoyée.");
          })} />
          <Button testID="delete-logout-button" title="Se déconnecter" variant="ghost" onClick={async () => { await logout(); navigate("/welcome", { replace: true }); }} />
        </div>
      </Sheet>
    </div>
  );
}
